import React, { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Copy, MoreVertical, Pencil, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { ActionMenu } from '../../components/ActionMenu';
import { CrmShell, PageHeader, Button, Modal } from '../../components/CrmShell';
import { EmptyState, Field, formatDate, inputClass, PaginationControls, parseDateInput, TABLE_PAGE_SIZE } from '../../components/CrmForms';
import { Activity, ActivityType, Company, Contact, Deal, Reminder } from '../../types';
import {
  createActivity,
  createContact,
  createReminder,
  deleteContact,
  deleteReminder,
  subscribeToActivitiesForContact,
  subscribeToCompanies,
  subscribeToContacts,
  subscribeToDeals,
  subscribeToReminders,
  updateContact,
  updateReminder,
} from '../../services/crm';
import { logGlobalError } from '../../utils/globalErrorLogger';

const ACTIVITY_TYPES: ActivityType[] = ['email', 'call', 'meeting', 'linkedin', 'note'];
const INBOUND_EMAIL_DOMAIN = process.env.REACT_APP_CRM_INBOUND_EMAIL_DOMAIN || 'mail.crm-aligned.com';

function contactBccAddress(contactId: string): string {
  return `contact-${contactId}@${INBOUND_EMAIL_DOMAIN}`;
}

export function ContactsPage() {
  const { contactId } = useParams();
  return contactId ? <ContactDetailPage contactId={contactId} /> : <ContactsListPage />;
}

function ContactsListPage() {
  const { currentUser } = useAuth();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Contact | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubs = [
      subscribeToContacts(setContacts, (err) => setError(err.message)),
      subscribeToCompanies(setCompanies, (err) => setError(err.message)),
    ];
    return () => unsubs.forEach((unsub) => unsub());
  }, []);

  const companyById = useMemo(() => new Map(companies.map((c) => [c.id, c])), [companies]);
  const filtered = contacts.filter((contact) => {
    const haystack = [
      contact.firstName,
      contact.lastName,
      contact.email,
      contact.companyId ? companyById.get(contact.companyId)?.name : '',
    ].join(' ').toLowerCase();
    return haystack.includes(query.toLowerCase());
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / TABLE_PAGE_SIZE));
  const pagedContacts = filtered.slice((page - 1) * TABLE_PAGE_SIZE, page * TABLE_PAGE_SIZE);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  async function saveContact(values: ContactFormValues) {
    const target = editing;
    const company = values.companyId ? companyById.get(values.companyId) : null;
    const payload = { ...values, companyName: company?.name ?? null };
    setEditing(null);
    try {
      if (target && target !== 'new') await updateContact(target.id, payload);
      else await createContact(payload, currentUser?.uid ?? '');
    } catch (err: any) {
      setError(err?.message || 'Failed to save contact');
      logGlobalError(err, { context: 'Contacts.saveContact' });
    }
  }

  async function removeContact(contact: Contact) {
    if (!window.confirm(`Delete ${contact.firstName} ${contact.lastName}?`)) return;
    await deleteContact(contact.id);
  }

  return (
    <CrmShell>
      <PageHeader
        title="Contacts"
        description="Create, search, and manage people in the CRM."
        action={<Button onClick={() => setEditing('new')}><Plus className="mr-1 inline h-4 w-4" />New contact</Button>}
      />
      {error && <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      <div className="mb-4">
        <input
          className={inputClass}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(1);
          }}
          placeholder="Search by name, email, or company..."
        />
      </div>
      {filtered.length ? (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-[760px] w-full text-sm">
              <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Company</th>
                  <th className="px-3 py-2">Tags</th>
                  <th className="px-3 py-2">Last activity</th>
                  <th className="px-3 py-2"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pagedContacts.map((contact) => (
                  <tr key={contact.id}>
                    <td className="px-3 py-2">
                      <Link className="font-medium text-primary hover:underline" to={`/contacts/${contact.id}`}>
                        {contact.firstName} {contact.lastName}
                      </Link>
                      <div className="text-xs text-gray-500">{contact.email}</div>
                    </td>
                    <td className="px-3 py-2 text-gray-700">{contact.companyId ? companyById.get(contact.companyId)?.name : '—'}</td>
                    <td className="px-3 py-2 text-gray-500">{contact.tags.join(', ') || '—'}</td>
                    <td className="px-3 py-2 text-gray-500">{formatDate(contact.lastActivityAt)}</td>
                    <td className="px-3 py-2 text-right">
                      <ActionMenu
                        label={`Actions for ${[contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.email || 'contact'}`}
                        items={[
                          { id: 'edit', label: 'Edit', icon: <Pencil className="h-4 w-4" />, onSelect: () => setEditing(contact) },
                          { id: 'delete', label: 'Delete', icon: <Trash2 className="h-4 w-4" />, onSelect: () => removeContact(contact), tone: 'danger' },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls page={page} totalItems={filtered.length} onPageChange={setPage} />
        </div>
      ) : <EmptyState>No contacts yet.</EmptyState>}

      {editing && (
        <Modal title={editing === 'new' ? 'New contact' : 'Edit contact'} onClose={() => setEditing(null)}>
          <ContactForm
            contact={editing === 'new' ? null : editing}
            companies={companies}
            onSubmit={saveContact}
          />
        </Modal>
      )}
    </CrmShell>
  );
}

function ContactDetailPage({ contactId }: { contactId: string }) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [editing, setEditing] = useState(false);
  const [editingReminder, setEditingReminder] = useState<Reminder | null>(null);
  const [activityOpen, setActivityOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubs = [
      subscribeToContacts(setContacts, (err) => setError(err.message)),
      subscribeToCompanies(setCompanies, (err) => setError(err.message)),
      subscribeToDeals(setDeals, (err) => setError(err.message)),
      subscribeToReminders(setReminders, (err) => setError(err.message)),
      subscribeToActivitiesForContact(contactId, setActivities, (err) => setError(err.message)),
    ];
    return () => unsubs.forEach((unsub) => unsub());
  }, [contactId]);

  const contact = contacts.find((item) => item.id === contactId) ?? null;
  const company = contact?.companyId ? companies.find((item) => item.id === contact.companyId) : null;
  const contactDeals = deals.filter((deal) => deal.contactId === contactId);
  const contactReminders = reminders.filter((reminder) => reminder.entityType === 'contact' && reminder.entityId === contactId);
  const bccAddress = contact ? contactBccAddress(contact.id) : '';

  async function saveActivity(values: ActivityFormValues) {
    setActivityOpen(false);
    try {
      await createActivity({ ...values, contactId }, currentUser?.uid ?? '');
    } catch (err: any) {
      setError(err?.message || 'Failed to log activity');
      logGlobalError(err, { context: 'ContactDetail.saveActivity' });
    }
  }

  async function saveReminder(values: ReminderFormValues) {
    setReminderOpen(false);
    try {
      await createReminder({ ...values, entityType: 'contact', entityId: contactId }, currentUser?.uid ?? '');
    } catch (err: any) {
      setError(err?.message || 'Failed to save reminder');
      logGlobalError(err, { context: 'ContactDetail.saveReminder' });
    }
  }

  async function completeReminder(reminder: Reminder) {
    try {
      await updateReminder(reminder.id, { completed: true });
    } catch (err: any) {
      setError(err?.message || 'Failed to complete reminder');
      logGlobalError(err, { context: 'ContactDetail.completeReminder' });
    }
  }

  async function saveEditedReminder(values: ReminderFormValues) {
    if (!editingReminder) return;
    const id = editingReminder.id;
    setEditingReminder(null);
    try {
      await updateReminder(id, values);
    } catch (err: any) {
      setError(err?.message || 'Failed to save reminder');
      logGlobalError(err, { context: 'ContactDetail.saveEditedReminder' });
    }
  }

  async function removeReminder(reminder: Reminder) {
    if (!window.confirm('Delete this reminder?')) return;
    try {
      await deleteReminder(reminder.id);
    } catch (err: any) {
      setError(err?.message || 'Failed to delete reminder');
      logGlobalError(err, { context: 'ContactDetail.deleteReminder' });
    }
  }

  async function saveContact(values: ContactFormValues) {
    const company = values.companyId ? companies.find((item) => item.id === values.companyId) : null;
    setEditing(false);
    try {
      await updateContact(contactId, { ...values, companyName: company?.name ?? null });
    } catch (err: any) {
      setError(err?.message || 'Failed to save contact');
      logGlobalError(err, { context: 'ContactDetail.saveContact' });
    }
  }

  async function removeContact() {
    if (!contact || !window.confirm(`Delete ${contact.firstName} ${contact.lastName}?`)) return;
    await deleteContact(contact.id);
    navigate('/contacts');
  }

  return (
    <CrmShell>
      <PageHeader
        title={contact ? `${contact.firstName} ${contact.lastName}` : 'Contact'}
        description={contact?.email}
        action={contact && <div className="flex flex-wrap gap-2"><Button variant="secondary" onClick={() => setEditing(true)}>Edit</Button><Button variant="secondary" onClick={() => setReminderOpen(true)}>Set reminder</Button><Button onClick={() => setActivityOpen(true)}>Log activity</Button><Button variant="danger" onClick={removeContact}>Delete</Button></div>}
      />
      {error && <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {!contact ? <EmptyState>Contact not found.</EmptyState> : (
        <div className="flex flex-col gap-6">
          <div className="grid items-stretch gap-6 lg:grid-cols-[2fr_1fr]">
            <section className="h-full rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-gray-900">Details</h2>
              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                <dl className="grid gap-3 text-sm sm:grid-cols-2 lg:col-span-2 xl:grid-cols-3">
                  <Detail label="Title" value={contact.title} />
                  <Detail label="Phone" value={contact.phone} />
                  <Detail label="Email" value={contact.email} />
                  <Detail label="Last activity" value={formatDate(contact.lastActivityAt)} />
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Company</dt>
                    <dd className="mt-1 text-gray-800">
                      {company ? <Link className="text-primary hover:underline" to={`/companies/${company.id}`}>{company.name}</Link> : '—'}
                    </dd>
                  </div>
                  <Detail label="Tags" value={contact.tags.join(', ')} />
                  <div className="sm:col-span-2">
                    <Detail label="Notes" value={contact.notes} />
                  </div>
                </dl>
                <div className="lg:col-span-2 rounded-md border border-gray-200 bg-gray-50 p-3">
                  <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Email logging BCC</dt>
                  <dd className="mt-2 flex items-center gap-2">
                    <code className="min-w-0 flex-1 truncate rounded border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-800">{bccAddress}</code>
                    <button
                      type="button"
                      aria-label="Copy email logging BCC address"
                      title="Copy email logging BCC address"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                      onClick={() => navigator.clipboard?.writeText(bccAddress)}
                    >
                      <Copy size={15} />
                    </button>
                  </dd>
                </div>
              </div>
              <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {contactDeals.length ? contactDeals.map((deal) => (
                  <Link key={deal.id} to={`/deals/${deal.id}`} className="block rounded border border-gray-200 px-3 py-2 text-sm hover:bg-gray-50">
                    <span className="font-medium text-gray-900">{deal.name}</span>
                    <span className="ml-2 text-gray-500">{deal.stage}</span>
                  </Link>
                )) : <p className="text-sm text-gray-500">No deals linked.</p>}
              </div>
            </section>
            <section className="flex h-full max-h-80 min-h-0 flex-col rounded-lg border border-gray-200 bg-white p-5 shadow-sm lg:max-h-none">
              <div className="flex shrink-0 items-center justify-between gap-3">
                <h2 className="text-sm font-semibold text-gray-900">Reminders</h2>
                <span className="text-xs font-medium text-gray-500">{contactReminders.length}</span>
              </div>
              <div className="mt-4 min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
                {contactReminders.length ? contactReminders.map((reminder) => (
                  <ContactReminderCard
                    key={reminder.id}
                    reminder={reminder}
                    onDone={() => completeReminder(reminder)}
                    onEdit={() => setEditingReminder(reminder)}
                    onDelete={() => removeReminder(reminder)}
                  />
                )) : <p className="text-sm text-gray-500">No reminders set.</p>}
              </div>
            </section>
          </div>
          <section className="flex h-[28rem] min-h-0 flex-col rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="shrink-0 text-sm font-semibold text-gray-900">Activity</h2>
            <div className="mt-4 min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain pr-1">
              {activities.length ? activities.map((activity) => (
                <article key={activity.id} className="rounded border border-gray-200 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-medium text-gray-900">{activity.subject || activity.type}</div>
                    <div className="text-xs text-gray-500">{formatDate(activity.activityDate)}</div>
                  </div>
                  <div className="mt-1 text-xs uppercase tracking-wide text-gray-400">{activity.type.replace('_', ' ')}</div>
                  {activity.body && <p className="mt-2 whitespace-pre-wrap text-sm text-gray-600">{activity.body}</p>}
                </article>
              )) : <p className="text-sm text-gray-500">No activity yet.</p>}
            </div>
          </section>
        </div>
      )}
      {activityOpen && (
        <Modal title="Log activity" onClose={() => setActivityOpen(false)}>
          <ActivityForm deals={contactDeals} onSubmit={saveActivity} />
        </Modal>
      )}
      {editing && contact && (
        <Modal title="Edit contact" onClose={() => setEditing(false)}>
          <ContactForm contact={contact} companies={companies} onSubmit={saveContact} />
        </Modal>
      )}
      {reminderOpen && (
        <Modal title="Set reminder" onClose={() => setReminderOpen(false)}>
          <ReminderForm onSubmit={saveReminder} currentUserId={currentUser?.uid ?? ''} />
        </Modal>
      )}
      {editingReminder && (
        <Modal title="Edit reminder" onClose={() => setEditingReminder(null)}>
          <ReminderForm
            onSubmit={saveEditedReminder}
            currentUserId={currentUser?.uid ?? ''}
            initialValues={{
              assignedTo: editingReminder.assignedTo,
              dueDate: editingReminder.dueDate,
              note: editingReminder.note,
            }}
          />
        </Modal>
      )}
    </CrmShell>
  );
}

function ContactReminderCard({
  reminder,
  onDone,
  onEdit,
  onDelete,
}: {
  reminder: Reminder;
  onDone: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  function runAction(action: () => void) {
    setMenuOpen(false);
    action();
  }

  return (
    <article className="relative rounded border border-gray-200 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm font-medium text-gray-900">{formatDate(reminder.dueDate)}</div>
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            aria-label="Reminder actions"
            className="flex h-7 w-7 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <MoreVertical className="h-4 w-4" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 z-10 mt-1 w-32 overflow-hidden rounded-md border border-gray-200 bg-white py-1 shadow-lg">
              <button type="button" className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:text-gray-400" disabled={reminder.completed} onClick={() => runAction(onDone)}>Done</button>
              <button type="button" className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50" onClick={() => runAction(onEdit)}>Edit</button>
              <button type="button" className="block w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50" onClick={() => runAction(onDelete)}>Delete</button>
            </div>
          )}
        </div>
      </div>
      {reminder.note && <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-gray-600">{reminder.note}</p>}
    </article>
  );
}

function Detail({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-1 whitespace-pre-wrap text-gray-800">{value || '—'}</dd>
    </div>
  );
}

interface ContactFormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  title: string;
  companyId: string | null;
  tags: string[];
  notes: string;
}

function ContactForm({ contact, companies, onSubmit }: { contact: Contact | null; companies: Company[]; onSubmit: (values: ContactFormValues) => Promise<void> }) {
  const [values, setValues] = useState<ContactFormValues>({
    firstName: contact?.firstName ?? '',
    lastName: contact?.lastName ?? '',
    email: contact?.email ?? '',
    phone: contact?.phone ?? '',
    title: contact?.title ?? '',
    companyId: contact?.companyId ?? null,
    tags: contact?.tags ?? [],
    notes: contact?.notes ?? '',
  });
  const [saving, setSaving] = useState(false);
  function set<K extends keyof ContactFormValues>(key: K, value: ContactFormValues[K]) {
    setValues((current) => ({ ...current, [key]: value }));
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try { await onSubmit(values); } finally { setSaving(false); }
  }
  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
      <Field label="First name"><input className={inputClass} value={values.firstName} onChange={(e) => set('firstName', e.target.value)} required /></Field>
      <Field label="Last name"><input className={inputClass} value={values.lastName} onChange={(e) => set('lastName', e.target.value)} required /></Field>
      <Field label="Email"><input className={inputClass} type="email" value={values.email} onChange={(e) => set('email', e.target.value)} /></Field>
      <Field label="Phone"><input className={inputClass} value={values.phone} onChange={(e) => set('phone', e.target.value)} /></Field>
      <Field label="Title"><input className={inputClass} value={values.title} onChange={(e) => set('title', e.target.value)} /></Field>
      <Field label="Company">
        <select className={inputClass} value={values.companyId ?? ''} onChange={(e) => set('companyId', e.target.value || null)}>
          <option value="">No company</option>
          {companies.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}
        </select>
      </Field>
      <div className="sm:col-span-2">
        <Field label="Tags">
          <input className={inputClass} value={values.tags.join(', ')} onChange={(e) => set('tags', e.target.value.split(',').map((v) => v.trim()).filter(Boolean))} placeholder="Prospect, Partner" />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <Field label="Notes"><textarea className={inputClass} rows={4} value={values.notes} onChange={(e) => set('notes', e.target.value)} /></Field>
      </div>
      <div className="sm:col-span-2"><Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save contact'}</Button></div>
    </form>
  );
}

interface ActivityFormValues {
  type: ActivityType;
  dealId: string | null;
  subject: string;
  body: string;
  activityDate: Date;
}

export function ActivityForm({ deals, onSubmit }: { deals: Deal[]; onSubmit: (values: ActivityFormValues) => Promise<void> }) {
  const [values, setValues] = useState<ActivityFormValues>({ type: 'note', dealId: null, subject: '', body: '', activityDate: new Date() });
  return (
    <form onSubmit={(e) => { e.preventDefault(); void onSubmit(values); }} className="space-y-4">
      <Field label="Type"><select className={inputClass} value={values.type} onChange={(e) => setValues({ ...values, type: e.target.value as ActivityType })}>{ACTIVITY_TYPES.map((type) => <option key={type} value={type}>{type === 'linkedin' ? 'LinkedIn' : type}</option>)}</select></Field>
      <Field label="Associated deal"><select className={inputClass} value={values.dealId ?? ''} onChange={(e) => setValues({ ...values, dealId: e.target.value || null })}><option value="">None</option>{deals.map((deal) => <option key={deal.id} value={deal.id}>{deal.name}</option>)}</select></Field>
      <Field label="Date"><input type="date" className={inputClass} value={values.activityDate.toISOString().slice(0, 10)} onChange={(e) => setValues({ ...values, activityDate: parseDateInput(e.target.value) ?? new Date() })} /></Field>
      <Field label="Subject"><input className={inputClass} value={values.subject} onChange={(e) => setValues({ ...values, subject: e.target.value })} /></Field>
      <Field label="Body"><textarea className={inputClass} rows={5} value={values.body} onChange={(e) => setValues({ ...values, body: e.target.value })} /></Field>
      <Button type="submit">Log activity</Button>
    </form>
  );
}

interface ReminderFormValues {
  assignedTo: string;
  dueDate: Date;
  note: string;
}

export function ReminderForm({
  onSubmit,
  currentUserId,
  initialValues,
}: {
  onSubmit: (values: ReminderFormValues) => Promise<void>;
  currentUserId: string;
  initialValues?: Partial<ReminderFormValues>;
}) {
  const [values, setValues] = useState<ReminderFormValues>({
    assignedTo: initialValues?.assignedTo ?? currentUserId,
    dueDate: initialValues?.dueDate ?? new Date(),
    note: initialValues?.note ?? '',
  });
  return (
    <form onSubmit={(e) => { e.preventDefault(); void onSubmit(values); }} className="space-y-4">
      <Field label="Due date"><input type="date" className={inputClass} value={values.dueDate.toISOString().slice(0, 10)} onChange={(e) => setValues({ ...values, dueDate: parseDateInput(e.target.value) ?? new Date() })} /></Field>
      <Field label="Note"><textarea className={inputClass} rows={4} value={values.note} onChange={(e) => setValues({ ...values, note: e.target.value })} /></Field>
      <Button type="submit">Save reminder</Button>
    </form>
  );
}
